<template>
  <div v-bind="rootAttrs" :class="rootClass">
    <el-image
      v-if="url"
      :key="imageKey"
      :src="url"
      :alt="alt"
      :lazy="lazy"
      :fit="fit"
      class="oss-img__inner"
      @load="handleImageLoad"
      @error="handleImageError"
    >
      <template #placeholder>
        <div class="oss-img__placeholder"></div>
      </template>
      <template #error>
        <div class="oss-img__placeholder"></div>
      </template>
    </el-image>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, useAttrs, watch } from "vue";
import { ossHelper } from "@/utils/oss";

const OSS_ENDPOINT = "oss-cn-hangzhou.aliyuncs.com";
const DEFAULT_EXPIRES_SECONDS = 600;
const MIN_REFRESH_DELAY_MS = 1000;

const props = defineProps({
  src: {
    type: String,
    default: "",
  },
  alt: {
    type: String,
    default: "",
  },
  class: {
    type: [String, Array, Object],
    default: "",
  },
  fit: {
    type: String,
    default: "contain",
  },
  lazy: {
    type: Boolean,
    default: true,
  },
  expires: {
    type: Number,
    default: DEFAULT_EXPIRES_SECONDS,
  },
  process: {
    type: String,
    default: "",
  },
  maxRetry: {
    type: Number,
    default: 3,
  },
  retryDelay: {
    type: Number,
    default: 1000,
  },
  recoveryDelay: {
    type: Number,
    default: 60 * 1000,
  },
  refreshBeforeExpire: {
    type: Number,
    default: 60,
  },
});

defineOptions({
  inheritAttrs: false,
});

const attrs = useAttrs();
const url = ref("");
const imageKey = ref(0);
const retryCount = ref(0);

let requestId = 0;
let retryTimer = null;
let refreshTimer = null;

const rootAttrs = computed(() => {
  const { class: className, ...restAttrs } = attrs;
  void className;
  return restAttrs;
});
const rootClass = computed(() => [attrs.class, props.class]);

function clearRetryTimer() {
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
}

function clearRefreshTimer() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

function clearTimers() {
  clearRetryTimer();
  clearRefreshTimer();
}

function isAssetAlias(src) {
  return src.includes("@/assets");
}

function isBundledAssetUrl(src) {
  return src.startsWith("/img/") || src.startsWith("img/") || src.startsWith("/css/") || src.startsWith("/js/");
}

function isDirectSource(src) {
  return (
    /^(data|blob):/.test(src) ||
    isBundledAssetUrl(src) ||
    (/^https?:\/\//.test(src) && !src.includes(OSS_ENDPOINT))
  );
}

function shouldSignSource(src) {
  const value = String(src || "").trim();
  return Boolean(value && !isAssetAlias(value) && !isDirectSource(value));
}

async function resolveImageUrl(src, forceRefresh) {
  const value = String(src || "").trim();
  if (!value) return "";

  if (isAssetAlias(value)) {
    return require(`@/assets${value.split("@/assets")[1]}`);
  }

  if (isDirectSource(value)) {
    return value;
  }

  return ossHelper.getAuthOssUrl(value, props.expires, props.process, { forceRefresh });
}

function scheduleRefresh() {
  clearRefreshTimer();

  if (!shouldSignSource(props.src)) return;

  const expiresMs = Math.max(Number(props.expires) || DEFAULT_EXPIRES_SECONDS, 1) * 1000;
  const refreshBeforeExpireMs = Math.max(Number(props.refreshBeforeExpire) || 0, 0) * 1000;
  const delay = Math.max(expiresMs - refreshBeforeExpireMs, MIN_REFRESH_DELAY_MS);

  refreshTimer = setTimeout(() => {
    loadImage({ forceRefresh: true });
  }, delay);
}

function scheduleRetry(error) {
  clearRefreshTimer();
  clearRetryTimer();

  if (!shouldSignSource(props.src)) return;

  if (retryCount.value >= props.maxRetry) {
    retryTimer = setTimeout(() => {
      retryCount.value = 0;
      loadImage({ forceRefresh: true });
    }, props.recoveryDelay);
    return;
  }

  const retryDelay = Math.max(props.retryDelay * 2 ** retryCount.value, MIN_REFRESH_DELAY_MS);
  retryCount.value += 1;

  retryTimer = setTimeout(() => {
    loadImage({ forceRefresh: true });
  }, retryDelay);

  console.warn("Retrying OSS image:", props.src, error);
}

async function loadImage(options = {}) {
  const { forceRefresh = false, resetRetry = false } = options;
  const currentSrc = props.src;
  const currentRequestId = ++requestId;

  clearRefreshTimer();

  if (resetRetry) {
    retryCount.value = 0;
  }

  if (!currentSrc) {
    url.value = "";
    return;
  }

  try {
    const nextUrl = await resolveImageUrl(currentSrc, forceRefresh);
    if (currentRequestId !== requestId) return;

    url.value = nextUrl;
    imageKey.value += 1;
    scheduleRefresh();
  } catch (error) {
    if (currentRequestId !== requestId) return;

    url.value = "";
    console.error("Failed to load OSS image:", error);
    scheduleRetry(error);
  }
}

function handleImageLoad() {
  retryCount.value = 0;
}

function handleImageError(error) {
  url.value = "";
  scheduleRetry(error);
}

watch(
  () => [props.src, props.expires, props.process],
  () => {
    clearTimers();
    loadImage({ resetRetry: true });
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  clearTimers();
  requestId += 1;
});
</script>

<style scoped>
.oss-img__inner,
.oss-img__placeholder {
  width: 100%;
  height: 100%;
}
</style>
